<template>
  <div class="p-6">
    <div class="mb-6">
      <button
        @click="goBack"
        class="inline-flex items-center gap-2 text-primary-600 hover:text-primary-800 mb-4"
      >
        <Icon name="heroicons:arrow-left" class="w-4 h-4" />
        {{ $t('common.back') }}
      </button>
      <p class="text-gray-600">{{ $t('admin.users.edit.subtitle') }}</p>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <!-- Success Alert -->
    <div v-if="successMessage" class="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded relative" role="alert">
      <span class="block sm:inline">{{ successMessage }}</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="text-center">
        <div class="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-gray-600">{{ $t('common.loading') }}</p>
      </div>
    </div>

    <!-- Edit Form -->
    <div v-else-if="user" class="bg-white rounded-lg shadow">
      <form @submit.prevent="handleSubmit" class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Given Name -->
          <div>
            <label for="givenName" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.givenName') }} *
            </label>
            <input
              id="givenName"
              v-model="form.givenName"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
              :class="{ 'border-red-500': errors.givenName }"
            />
            <p v-if="errors.givenName" class="mt-1 text-sm text-red-600">{{ errors.givenName }}</p>
          </div>

          <!-- Family Name -->
          <div>
            <label for="familyName" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.familyName') }} *
            </label>
            <input
              id="familyName"
              v-model="form.familyName"
              type="text"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
              :class="{ 'border-red-500': errors.familyName }"
            />
            <p v-if="errors.familyName" class="mt-1 text-sm text-red-600">{{ errors.familyName }}</p>
          </div>

          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.email') }} *
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
              :class="{ 'border-red-500': errors.email }"
            />
            <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
          </div>

          <!-- Nickname -->
          <div>
            <label for="nickname" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.nickname') }}
            </label>
            <input
              id="nickname"
              v-model="form.nickname"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
            />
          </div>

          <!-- Phone Number -->
          <div>
            <label for="phoneNumber" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.phoneNumber') }}
            </label>
            <input
              id="phoneNumber"
              v-model="form.phoneNumber"
              type="tel"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
            />
          </div>

          <!-- Role -->
          <div>
            <label for="role" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.role') }} *
            </label>
            <select
              id="role"
              v-model="form.roleType"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
              :class="{ 'border-red-500': errors.roleType }"
            >
              <option value="">{{ $t('admin.users.form.selectRole') }}</option>
              <option
                v-for="role in roles"
                :key="role.id"
                :value="role.role_name"
              >
                {{ getOptionLabel(role.role_name) }}
              </option>
            </select>
            <p v-if="errors.roleType" class="mt-1 text-sm text-red-600">{{ errors.roleType }}</p>
          </div>

          <!-- Status -->
          <div>
            <label for="status" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('admin.users.form.status') }}
            </label>
            <select
              id="status"
              v-model="form.active"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-700 bg-white"
            >
              <option :value="1">{{ $t('admin.users.status.active') }}</option>
              <option :value="0">{{ $t('admin.users.status.inactive') }}</option>
            </select>
          </div>


        </div>

        <!-- Form Actions -->
        <div class="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            @click="goBack"
            class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            type="submit"
            :disabled="submitting"
            class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <span v-if="submitting" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            {{ submitting ? $t('common.saving') : $t('common.save') }}
          </button>
        </div>
      </form>
    </div>

    <!-- User Not Found -->
    <div v-else class="text-center py-12">
      <div class="text-gray-400 mb-4">
        <Icon name="heroicons:user" class="w-16 h-16 mx-auto" />
      </div>
      <h3 class="text-lg font-medium text-gray-900 mb-2">{{ $t('admin.users.edit.notFound') }}</h3>
      <p class="text-gray-600 mb-4">{{ $t('admin.users.edit.notFoundMessage') }}</p>
      <button
        @click="goBack"
        class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
      >
        {{ $t('common.back') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { userService } from '~/services/api/users'
import { roleService } from '~/services/api/roles'
import type { UpdateUserPayload } from '~/types/user'

// Define interfaces
interface ApiRole {
  id: number
  role_name: string
}

interface ApiUser {
  id: number
  login: string
  given_name: string
  family_name: string
  email: string
  nickname?: string
  phone_number?: string
  role: string
  activated: number // 1 for active, 0 for inactive
}

interface FormErrors {
  givenName?: string
  familyName?: string
  email?: string
  roleType?: string
}

// Get route and router
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

// Reactive data
const user = ref<ApiUser | null>(null)
const roles = ref<ApiRole[]>([])
const loading = ref(false)
const submitting = ref(false)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)

// Form data
const form = ref<UpdateUserPayload & { givenName: string; familyName: string }>({
  givenName: '',
  familyName: '',
  email: '',
  nickname: '',
  phoneNumber: '',
  roleType: '',
  active: 1
})

// Form errors
const errors = ref<FormErrors>({})

// Get user ID from route
const userId = computed(() => parseInt(route.params.id as string))

// Fetch user data
const fetchUser = async () => {
  try {
    loading.value = true
    error.value = null
    
    // Get user directly from the API using the specific endpoint
    const userData = await userService.getUser(userId.value)
    
    if (!userData) {
      throw new Error('User not found')
    }
    
    console.log('🔍 Raw user data from API:', userData)
    
    // Map API response to our format
    user.value = {
      id: userData.id,
      login: userData.login,
      given_name: userData.given_name,
      family_name: userData.family_name,
      email: userData.email,
      nickname: userData.nickname,
      phone_number: userData.phone_number,
      role: userData.role,
      activated: userData.activated
    }
    
    // Populate form
    if (user.value) {
      form.value = {
        givenName: user.value.given_name,
        familyName: user.value.family_name,
        email: user.value.email,
        nickname: user.value.nickname || '',
        phoneNumber: user.value.phone_number || '',
        roleType: user.value.role,
        active: user.value.activated
      }
    }
    
    console.log('🔍 User data:', user.value)
    console.log('🔍 Form data:', form.value)
    console.log('🔍 Status value:', form.value.active, typeof form.value.active)
    console.log('🔍 User activated value:', user.value.activated, typeof user.value.activated)
    
  } catch (err) {
    error.value = t('admin.users.edit.error.fetch_failed')
    console.error('Failed to fetch user:', err)
  } finally {
    loading.value = false
  }
}

// Fetch roles
const fetchRoles = async () => {
  try {
    const rolesData = await roleService.getRoles()
    roles.value = rolesData.map(role => ({
      id: role.id,
      role_name: role.role_name
    }))
  } catch (err) {
    console.error('Failed to fetch roles:', err)
  }
}

// Form validation
const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.givenName?.trim()) {
    errors.value.givenName = t('admin.users.form.errors.givenNameRequired')
  }
  
  if (!form.value.familyName?.trim()) {
    errors.value.familyName = t('admin.users.form.errors.familyNameRequired')
  }
  
  if (!form.value.email?.trim()) {
    errors.value.email = t('admin.users.form.errors.emailRequired')
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
    errors.value.email = t('admin.users.form.errors.emailInvalid')
  }
  
  if (!form.value.roleType) {
    errors.value.roleType = t('admin.users.form.errors.roleRequired')
  }
  
  return Object.keys(errors.value).length === 0
}

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }
  
  try {
    submitting.value = true
    error.value = null
    successMessage.value = null
    
    // Prepare update payload
    const updatePayload: UpdateUserPayload = {
      givenName: form.value.givenName,
      familyName: form.value.familyName,
      email: form.value.email,
      nickname: form.value.nickname || undefined,
      phoneNumber: form.value.phoneNumber || undefined,
      roleType: form.value.roleType,
      active: form.value.active
    }
    
    // Call API to update user
    await userService.updateUser(userId.value, updatePayload)
    
    // Show success message
    successMessage.value = t('admin.users.edit.success.update_success')
    
    // Auto-hide success message after 3 seconds and navigate back
    setTimeout(() => {
      successMessage.value = null
      router.push('/admin/users')
    }, 1000)
    
  } catch (err) {
    error.value = t('admin.users.edit.error.update_failed')
    console.error('Failed to update user:', err)
  } finally {
    submitting.value = false
  }
}

// Navigation
const goBack = () => {
  router.push('/admin/users')
}

// Helper function for role labels
const getOptionLabel = (role: string) => {
  return t(`admin.users.roles.${role}`)
}

// Watch for form.active changes to debug
watch(() => form.value.active, (newValue, oldValue) => {
  console.log('🔍 Form active value changed:', { oldValue, newValue, type: typeof newValue })
})

// Initialize data
onMounted(async () => {
  await Promise.all([
    fetchUser(),
    fetchRoles()
  ])
})

// Page meta
definePageMeta({
  layout: 'admin'
})
</script> 